import { Flex, Icon, VStack, Box, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { AppNameProvider, AppName } from '@m43/core';
import { DexUpdateToEvent } from '../update-to-event/dex-update-to-event';
import { ResultHeader } from './result-header';
import { ResultLines } from './result-lines';
import { useDexResults } from './use-dex-results';
import { useUpdateToEventKeyboardHandler } from './use-update-to-event-keyboard-handler';
import { EventAutocompleteComponent } from '../components/event-autocomplete';
import { ResultSummary } from './result-summary';
import { DexParsedPersonSummary } from './dex-parsed-person-summary';
import { DexParsedVehicleSummary } from './dex-parsed-vehicle-summary';
import { isHit } from '../utils';

const DexResultsRightPaneWrapper = ({
    testId,
    children,
}: React.PropsWithChildren<{ testId?: string }>) => (
    <Box
        data-testid={testId}
        bgColor="surface.canvas"
        w="100%"
        h="100%"
        padding="16px"
        borderLeft="1px solid var(--arc-colors-border-default)"
    >
        {children}
    </Box>
);
export const DexResultsRightPane = observer(function DexResultsRightPane({
    scrollContainerRef,
    testId,
}: {
    scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    testId?: string;
}): React.ReactElement {
    const { dexResultsStore } = useDexResults();
    const { showEntitiesPopoverRef } = useUpdateToEventKeyboardHandler();

    const selectedQueryAndResult = dexResultsStore.selectedQueryAndResult;
    const selectedQueryAndSummary = dexResultsStore.selectedQueryAndSummary;
    const selectedIncomingMessageId = dexResultsStore.getSelectedResultId();
    const selectedSummaryId = dexResultsStore.getSelectedSummaryId();
    const isSummaryView = !!selectedSummaryId;
    const dexMessageId = isSummaryView ? selectedSummaryId : selectedIncomingMessageId;
    const showNoResultSelected = !selectedQueryAndResult && !selectedQueryAndSummary;

    const dexUpdateToEvent = (
        <Box align="end">
            <AppNameProvider appName={AppName.CAD_NEXT}>
                <DexUpdateToEvent
                    key={dexMessageId}
                    dexMessageId={dexMessageId}
                    isSummaryView={isSummaryView}
                    showEntitiesPopoverRef={showEntitiesPopoverRef}
                    EventAutocompleteComponent={EventAutocompleteComponent}
                    showLeadingVisualIcon
                />
            </AppNameProvider>
        </Box>
    );

    useEffect(
        function scrollSelectedResultToTopOnChange() {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedIncomingMessageId]
    );

    if (showNoResultSelected) {
        return (
            <DexResultsRightPaneWrapper testId={testId}>
                <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
                    <VStack>
                        <Icon icon="Dex" size="lg" color="icon.default" />
                        <Text>No result selected</Text>
                    </VStack>
                </Flex>
            </DexResultsRightPaneWrapper>
        );
    }

    return (
        <DexResultsRightPaneWrapper testId={testId}>
            <VStack align="left" w="100%" h="100%">
                {selectedQueryAndResult && (
                    <>
                        <ResultHeader
                            query={selectedQueryAndResult.query}
                            isHit={isHit(selectedQueryAndResult.result)}
                            result={selectedQueryAndResult.result}
                        >
                            {dexUpdateToEvent}
                        </ResultHeader>
                        <VStack
                            align="left"
                            w="100%"
                            overflowY="auto"
                            paddingRight="0.5rem"
                            gap="1rem"
                            ref={scrollContainerRef}
                        >
                            {selectedQueryAndResult.result.summaryViews ? (
                                <ResultSummary
                                    summaryViews={selectedQueryAndResult.result.summaryViews}
                                />
                            ) : undefined}
                            <ResultLines
                                result={selectedQueryAndResult.result}
                                originatingQueryOriAliasId={
                                    selectedQueryAndResult?.query?.oriAliasId
                                }
                            />
                        </VStack>
                    </>
                )}
                {selectedQueryAndSummary && (
                    <>
                        <ResultHeader
                            query={selectedQueryAndSummary.query}
                            isHit={selectedQueryAndSummary.summary.isHit}
                        >
                            {dexUpdateToEvent}
                        </ResultHeader>
                        <VStack
                            align="left"
                            w="100%"
                            overflowY="auto"
                            paddingRight="0.5rem"
                            ref={scrollContainerRef}
                        >
                            {selectedQueryAndSummary.summary.vehicleRegistrationResult && (
                                <DexParsedVehicleSummary
                                    vehicle={
                                        selectedQueryAndSummary.summary.vehicleRegistrationResult
                                    }
                                />
                            )}
                            {selectedQueryAndSummary.summary.personSummaryViews.map(
                                ({ rmvDriverLicenseResult, rmsPersonProfileSummary, rmvImage }) => {
                                    return (
                                        <DexParsedPersonSummary
                                            key={rmvDriverLicenseResult.operatorLicenseNumber}
                                            rmvDriverLicenseResult={rmvDriverLicenseResult}
                                            rmsPersonProfileSummary={rmsPersonProfileSummary}
                                            rmvImage={rmvImage}
                                        />
                                    );
                                }
                            )}
                        </VStack>
                    </>
                )}
            </VStack>
        </DexResultsRightPaneWrapper>
    );
});
